import { GameState, InningsState } from '../types/game';

export const calculateProjectedScore = (innings: InningsState, settings: GameState['matchSettings']) => {
  if (innings.totalBalls === 0) return 0;
  const totalBallsInInnings = settings.oversPerInnings * settings.ballsPerOver;
  const runRate = innings.runs / (innings.totalBalls / settings.ballsPerOver);
  return Math.round(runRate * settings.oversPerInnings);
};

export const calculateTarget = (innings1: InningsState) => {
  return innings1.runs + 1;
};

export const calculateRequiredRunRate = (gameState: GameState) => {
  if (gameState.currentInnings !== 2 || !gameState.innings2) return "0.0";
  
  const target = calculateTarget(gameState.innings1);
  const runsNeeded = target - gameState.innings2.runs;
  const ballsRemaining = (gameState.matchSettings.oversPerInnings * gameState.matchSettings.ballsPerOver) - gameState.innings2.totalBalls;
  
  if (ballsRemaining <= 0) return "0.0";
  
  const oversRemaining = ballsRemaining / gameState.matchSettings.ballsPerOver;
  return (runsNeeded / oversRemaining).toFixed(1);
};

export const checkInningsComplete = (innings: InningsState, gameState: GameState) => {
  const maxBalls = gameState.matchSettings.oversPerInnings * gameState.matchSettings.ballsPerOver;
  const maxWickets = gameState.matchSettings.numberOfPlayers - 1;
  
  // Check if all balls are bowled
  if (innings.totalBalls >= maxBalls) return true;
  
  // If Last Man Stands is enabled, innings ends only when the last batter is out
  if (gameState.matchSettings.customRules.enableLastManStands) {
    return innings.wicketsDown >= gameState.matchSettings.numberOfPlayers;
  }
  
  // Normal cricket rules - innings ends when all but one wicket is down
  return innings.wicketsDown >= maxWickets;
};

export const checkGameComplete = (gameState: GameState) => {
  if (gameState.currentInnings === 1) {
    return checkInningsComplete(gameState.innings1, gameState);
  } else if (gameState.currentInnings === 2 && gameState.innings2) {
    const target = calculateTarget(gameState.innings1);
    return checkInningsComplete(gameState.innings2, gameState) || gameState.innings2.runs >= target;
  }
  return false;
};

export const checkLastManStands = (innings: InningsState, gameState: GameState) => {
  if (!gameState.matchSettings.customRules.enableLastManStands) return false;
  
  const maxWickets = gameState.matchSettings.numberOfPlayers - 1;
  return innings.wicketsDown >= maxWickets;
};

export const determineWinner = (state: GameState) => {
  if (!state.innings2) return undefined;
  
  const target = state.innings1.runs + 1;
  
  if (state.innings2.runs >= target) {
    return state.battingTeam === 'A' ? state.teamA : state.teamB;
  } else {
    return state.battingTeam === 'A' ? state.teamB : state.teamA;
  }
};

export const getNextBatterName = (currentBatters: [string, string], innings: InningsState, allPlayers: string[]): string => {
  const availablePlayers = allPlayers.filter(
    player => !currentBatters.includes(player) && 
    !innings.wicketRecords.some(w => w.batterOut === player)
  );
  return availablePlayers[0] || "New Batter";
};

export const getNextBowlerName = (currentBowler: string, bowlingPlayers: string[]): string => {
  const currentBowlerIndex = bowlingPlayers.indexOf(currentBowler);
  const nextIndex = (currentBowlerIndex + 1) % bowlingPlayers.length;
  return bowlingPlayers[nextIndex];
};

export const formatOversDisplay = (totalBalls: number, ballsPerOver: number): string => {
  const totalOvers = Math.floor(totalBalls / ballsPerOver);
  const ballsInCurrentOver = totalBalls % ballsPerOver;
  return `${totalOvers}.${ballsInCurrentOver}`;
};

export const formatRunRate = (runs: number, totalBalls: number, ballsPerOver: number): string => {
  return totalBalls > 0 
    ? (runs / (totalBalls / ballsPerOver)).toFixed(1)
    : "0.0";
};

export const calculateWicketsRemaining = (wicketsDown: number, numberOfPlayers: number): number => {
  return numberOfPlayers - 1 - wicketsDown;
};

export const startSecondInnings = (gameState: GameState): GameState => {
  const newState = { ...gameState };
  newState.currentInnings = 2;
  newState.battingTeam = newState.battingTeam === 'A' ? 'B' : 'A';
  newState.bowlingTeam = newState.bowlingTeam === 'A' ? 'B' : 'A';
  
  const battingPlayers = newState.battingTeam === 'A' ? newState.teamAPlayers : newState.teamBPlayers;
  const bowlingPlayers = newState.bowlingTeam === 'A' ? newState.teamAPlayers : newState.teamBPlayers;
  
  const { createInitialInningsState } = require('../constants/game');
  newState.innings2 = createInitialInningsState(battingPlayers, bowlingPlayers);
  
  return newState;
};